import { useEffect, useState } from 'react';

interface UseInitialLoadingOptions {
  /**
   * Minimum loading time in milliseconds to prevent flashing
   */
  minLoadingTime?: number;

  /**
   * Additional delay after loading completes (for smooth transitions)
   */
  fadeOutDelay?: number;

  /**
   * Debug mode to log loading events
   */
  debug?: boolean;
}

/**
 * Hook to track initial application loading state
 * Waits for document readiness, fonts, and critical resources
 */
export function useInitialLoading({
  minLoadingTime = 800,
  fadeOutDelay = 200,
  debug = false,
}: UseInitialLoadingOptions = {}): boolean {
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const startTime = performance.now();
    let timeout: number | undefined;

    const logDebug = (message: string): void => {
      if (debug) {
        console.debug(`[InitialLoading] ${message}`);
      }
    };

    const completeLoading = (): void => {
      const elapsedTime = performance.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      logDebug(`Resources loaded after ${elapsedTime.toFixed(0)}ms`);

      if (remainingTime > 0) {
        logDebug(
          `Waiting additional ${remainingTime.toFixed(0)}ms to meet minimum loading time`,
        );
      }

      // Ensure minimum loading time is respected
      timeout = window.setTimeout(() => {
        logDebug(
          `Completing initial loading after ${minLoadingTime}ms + ${fadeOutDelay}ms fade delay`,
        );
        setIsLoading(false);
      }, remainingTime + fadeOutDelay);
    };

    // Check if document is already complete
    if (document.readyState === 'complete') {
      logDebug('Document already complete on mount');
      completeLoading();
      return;
    }

    // Wait for document to be fully loaded
    const handleLoad = (): void => {
      logDebug('Window load event fired');
      completeLoading();
    };

    // Listen for load event
    window.addEventListener('load', handleLoad);

    // Check font loading status
    if ('fonts' in document) {
      logDebug('Waiting for fonts to load');
      document.fonts.ready
        .then(() => {
          logDebug('Fonts loaded successfully');
        })
        .catch((err) => {
          logDebug(`Error loading fonts: ${err.message}`);
        });
    }

    return () => {
      window.removeEventListener('load', handleLoad);
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, [minLoadingTime, fadeOutDelay, debug]);

  return isLoading;
}
